import { Theme, ThemedStyles } from 'ui/base/theme';

import { Optional } from 'services/base/types';

import profileIconImageActiveMidnight from './deployment_profiles_icons/active/dark.png';
import profileIconImageActiveDaylight from './deployment_profiles_icons/active/light.png';
import profileIconImagePassiveMidnight from './deployment_profiles_icons/passive/dark.png';
import profileIconImagePassiveDaylight from './deployment_profiles_icons/passive/light.png';
import profileIconImageCustomMidnight from './deployment_profiles_icons/custom/dark.png';
import profileIconImageCustomDaylight from './deployment_profiles_icons/custom/light.png';

import profileGraphImageActiveFullMidnight from './deployment_profiles_graphs/active_full/dark.png';
import profileGraphImageActiveFullDaylight from './deployment_profiles_graphs/active_full/light.png';
import profileGraphImageActiveInternetMidnight from './deployment_profiles_graphs/active_internet/dark.png';
import profileGraphImageActiveInternetDaylight from './deployment_profiles_graphs/active_internet/light.png';
import profileGraphImagePassiveMidnight from './deployment_profiles_graphs/passive/dark.png';
import profileGraphImagePassiveDaylight from './deployment_profiles_graphs/passive/light.png';

import Messages from './deployment_profiles_messages';

export enum DeploymentProfile {
  PASSIVE = 'passive',
  ACTIVE_FULL = 'active_full',
  ACTIVE_INTERNET = 'active_internet',
}

export type DeploymentSettings = Partial<{
  enablePhysicalIntercept: boolean;
  enableUnicastSpoofing: boolean;
  useUnicastRequest: boolean;
  useUnicastResponse: boolean;
  redirectInternetWithUnicast: boolean;
  redirectInternalWithUnicast: boolean;
  enableBroadcastSpoofing: boolean;
  useBroadcastRequest: boolean;
  useBroadcastResponse: boolean;
  spoofGatewayWithBroadcast: boolean;
  spoofHostsWithBroadcast: boolean;
  masqueradeEgress: boolean;
}>;

export const deploymentProfileSettings: Record<DeploymentProfile, DeploymentSettings> = {
  [DeploymentProfile.PASSIVE]: {
    enablePhysicalIntercept: true,
    enableUnicastSpoofing: false,
    enableBroadcastSpoofing: false,
  },
  [DeploymentProfile.ACTIVE_FULL]: {
    enableUnicastSpoofing: true,
    useUnicastRequest: true,
    useUnicastResponse: true,
    redirectInternetWithUnicast: true,
    redirectInternalWithUnicast: true,
    enableBroadcastSpoofing: true,
    useBroadcastRequest: true,
    useBroadcastResponse: true,
    spoofGatewayWithBroadcast: true,
  },
  [DeploymentProfile.ACTIVE_INTERNET]: {
    enableUnicastSpoofing: true,
    useUnicastRequest: true,
    useUnicastResponse: true,
    redirectInternetWithUnicast: true,
    redirectInternalWithUnicast: false,
    enableBroadcastSpoofing: true,
    useBroadcastRequest: true,
    useBroadcastResponse: true,
    spoofGatewayWithBroadcast: true,
    spoofHostsWithBroadcast: false,
  },
};

/**
 * Get the deployment profile that matches the given settings.
 * @param settings - The settings to match.
 * @returns The deployment profile that matches the given settings, or undefined if no match is found.
 */
export const getDeploymentSettingsProfile = (settings: DeploymentSettings): Optional<DeploymentProfile> => {
  for (const profile of Object.values(DeploymentProfile)) {
    let isMatch = true;
    const presets = deploymentProfileSettings[profile];
    for (const [profileKey, profileValue] of Object.entries(presets)) {
      if (profileValue === undefined) continue;
      const settingsKey = profileKey as keyof typeof settings;
      if (!Object.hasOwn(settings, settingsKey) || profileValue !== settings[settingsKey]) {
        isMatch = false;
        break;
      }
    }
    if (isMatch) {
      return profile;
    }
  }
  return undefined;
};

export const getDeploymentProfileName = (profile: Optional<DeploymentProfile>): string => {
  switch(profile) {
    case DeploymentProfile.PASSIVE:
      return Messages.profilePassiveName();
    case DeploymentProfile.ACTIVE_FULL:
      return Messages.profileActiveFullName();
    case DeploymentProfile.ACTIVE_INTERNET:
      return Messages.profileActiveInternetName();
    default:
      return Messages.profileCustomName();
  }
};

export const getDeploymentProfileSummary = (profile: DeploymentProfile): string => {
  switch(profile) {
    case DeploymentProfile.PASSIVE:
      return Messages.profilePassiveSummary();
    case DeploymentProfile.ACTIVE_FULL:
      return Messages.profileActiveFullSummary();
    case DeploymentProfile.ACTIVE_INTERNET:
      return Messages.profileActiveInternetSummary();
    default:
      return Messages.profileCustomSummary();
  }
};

export const getDeploymentProfileDetails = (profile: Optional<DeploymentProfile>): string => {
  switch(profile) {
    case DeploymentProfile.PASSIVE:
      return Messages.profilePassiveDetails();
    case DeploymentProfile.ACTIVE_FULL:
      return Messages.profileActiveFullDetails();
    case DeploymentProfile.ACTIVE_INTERNET:
      return Messages.profileActiveInternetDetails();
    default:
      return Messages.profileCustomDetails();
  }
};

export const getDeploymentProfileGuide = (profile: DeploymentProfile): Optional<string> => {
  switch(profile) {
    case DeploymentProfile.PASSIVE:
      return Messages.profilePassiveGuide();
    case DeploymentProfile.ACTIVE_FULL:
      return Messages.profileActiveFullGuide();
    case DeploymentProfile.ACTIVE_INTERNET:
      return Messages.profileActiveInternetGuide();
    default:
      return undefined;
  }
};

export const getDeploymentProfileWarning = (profile: DeploymentProfile): Optional<string> => {
  switch(profile) {
    case DeploymentProfile.PASSIVE:
      return Messages.profilePassiveWarning();
    case DeploymentProfile.ACTIVE_FULL:
      return Messages.profileActiveFullWarning();
    case DeploymentProfile.ACTIVE_INTERNET:
      return Messages.profileActiveInternetWarning();
    default:
      return undefined;
  }
};

export type DeploymentProfileIconStyles = {
  passive: string;
  activeFull: string;
  activeInternet: string;
  custom: string;
};

export const themedDeploymentProfileIconImageStyles: ThemedStyles<DeploymentProfileIconStyles> = {
  [Theme.DAYLIGHT]: {
    passive: profileIconImagePassiveDaylight,
    activeFull: profileIconImageActiveDaylight,
    activeInternet: profileIconImageActiveDaylight,
    custom: profileIconImageCustomDaylight,
  },
  [Theme.MIDNIGHT]: {
    passive: profileIconImagePassiveMidnight,
    activeFull: profileIconImageActiveMidnight,
    activeInternet: profileIconImageActiveMidnight,
    custom: profileIconImageCustomMidnight,
  },
};

export const getDeploymentProfileIconImage = (styles: DeploymentProfileIconStyles, profile: Optional<DeploymentProfile>): string => {
  switch(profile) {
    case DeploymentProfile.PASSIVE:
      return styles.passive;
    case DeploymentProfile.ACTIVE_FULL:
      return styles.activeFull;
    case DeploymentProfile.ACTIVE_INTERNET:
      return styles.activeInternet;
    default:
      return styles.custom;
  }
};

export type DeploymentProfileGraphImageStyles = {
  passive: string;
  activeFull: string;
  activeInternet: string;
};

export const themedDeploymentProfileGraphImageStyles: ThemedStyles<DeploymentProfileGraphImageStyles> = {
  [Theme.DAYLIGHT]: {
    passive: profileGraphImagePassiveDaylight,
    activeFull: profileGraphImageActiveFullDaylight,
    activeInternet: profileGraphImageActiveInternetDaylight,
  },
  [Theme.MIDNIGHT]: {
    passive: profileGraphImagePassiveMidnight,
    activeFull: profileGraphImageActiveFullMidnight,
    activeInternet: profileGraphImageActiveInternetMidnight,
  },
};

export const getDeploymentProfileGraphImage = (styles: DeploymentProfileGraphImageStyles, profile: DeploymentProfile): Optional<string> => {
  switch(profile) {
    case DeploymentProfile.PASSIVE:
      return styles.passive;
    case DeploymentProfile.ACTIVE_FULL:
      return styles.activeFull;
    case DeploymentProfile.ACTIVE_INTERNET:
      return styles.activeInternet;
    default:
      return undefined;
  }
};

export const getDeploymentProfileGraphCoverage = (profile: DeploymentProfile): [boolean, boolean, boolean, boolean, boolean] => {
  switch (profile) {
    case DeploymentProfile.PASSIVE:
      return [true, true, true, false, false];
    case DeploymentProfile.ACTIVE_FULL:
      return [true, true, true, true, true];
    case DeploymentProfile.ACTIVE_INTERNET:
      return [true, true, false, false, false];
    default:
      return [false, false, false, false, false];
  }
};
